import type { ReactElement } from 'react';
import React from 'react';

import styled from '@emotion/styled';
import type { ComponentPropsWithoutRef } from '@rtkwlf/fenrir-react';
import { colors, Primitive } from '@rtkwlf/fenrir-react';
import ReactMarkdown from 'react-markdown';
import type { PluggableList } from 'react-markdown/lib';

export interface MarkdownProps
  extends ComponentPropsWithoutRef<typeof Primitive.div> {
  value?: string;
  components?: object;
  markdownPlugins?: PluggableList;
  rehypePlugins?: PluggableList;
}

// partially hard-coded to follow Fenrir styles
const StyledDiv = styled(Primitive.div)`
  overflow-x: scroll;
  padding: 1px;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: 'Encode Sans';
    font-weight: bold;
    line-height: 1em;
    word-break: break-word;
  }
  h1 {
    font-size: 36px;
  }
  h2 {
    font-size: 24px;
  }
  h3 {
    font-size: 18px;
  }
  h4 {
    font-size: 16px;
  }
  h5 {
    font-size: 14px;
  }
  h6 {
    font-size: 12px;
  }
  p,
  ul,
  ol,
  blockquote,
  code {
    word-break: break-word;
  }
  p {
    font-family: 'Lato';
    font-size: 14px;
    margin-top: 0px;
  }
  ul,
  ol {
    font-size: 14px;
    padding: 0 0 0 1.5em;
  }
  ul {
    list-style-type: disc;
  }
  ol {
    list-style-type: decimal;
  }

  li > ul,
  li > ol {
    padding-left: 1.5em;
    list-style-type: circle;
  }

  li > ol {
    list-style-type: lower-alpha;
  }

  /* Adjust padding for nested lists */
  li > ul,
  li > ol {
    margin-top: 0.5em;
    margin-bottom: 0;
    padding-left: 1.5em;
  }
  blockquote {
    background-color: rgb(${colors.background.widget.tertiary});
    border-left: 8px solid rgb(${colors.border.neutral.default});
    border-radius: 4px;
    color: rgb(${colors.text.default.body});
    line-height: 1em;
    margin: 1.5em 0.5em;
    padding: 1em;
  }
  code {
    font-family: 'Courier';
    font-size: 16px;
    padding: 0 8px;
    border-radius: 4px;
    word-break: break-word;
    overflow-x: scroll;
    color: rgb(${colors.text.brand.default});
    background-color: rgb(${colors.background.widget.tertiary});
    border: 1px solid rgb(${colors.border.neutral.secondary});
  }
  pre {
    padding: 1rem;
    white-space: pre-wrap;
    background-color: rgb(${colors.background.widget.tertiary});
  }

  /* Table styles for markdown table */
  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    margin: 15px 0;
    font-size: 14px;
    border: 1px solid rgb(${colors.border.neutral.default});
    border-radius: 4px;
    overflow: hidden;
  }

  th {
    text-align: left;
    font-weight: bold;
    background-color: rgb(${colors.background.widget.secondary});
    color: rgb(${colors.text.default.body});
    padding: 12px;
    border-bottom: 2px solid rgb(${colors.border.neutral.default});
  }

  td {
    padding: 12px;
    border-bottom: 1px solid rgb(${colors.border.neutral.tertiary}, 0.3);
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:nth-child(even) {
    background-color: rgb(${colors.background.widget.tertiary});
  }

  tr:nth-child(odd) {
    background-color: rgb(${colors.background.widget.default});
  }

  tr:hover {
    background-color: rgb(${colors.background.widget.highlight});
    transition: background-color 0.3s ease;
  }
`;

export const Markdown = ({
  value = '',
  components = {},
  markdownPlugins = [],
  rehypePlugins = [],
  ...rest
}: MarkdownProps): ReactElement => (
  <StyledDiv textColor="body" {...rest}>
    <ReactMarkdown
      components={components}
      remarkPlugins={markdownPlugins}
      rehypePlugins={rehypePlugins}
    >
      {value}
    </ReactMarkdown>
  </StyledDiv>
);
